import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageSquare, User as UserIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';
import { useSettings } from '../lib/settings';
import FloatingBadge from '../components/three/FloatingBadge';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', course: '', message: '' });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { settings } = useSettings();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast({ title: 'Please fill required fields', variant: 'destructive' }); return; }
    setLoading(true);
    try {
      await api.contact(form);
      toast({ title: 'Message sent!', description: 'Our team will reach out within 24 hours.' });
      setForm({ name: '', email: '', phone: '', course: '', message: '' });
    } catch (e) {
      toast({ title: 'Failed to send. Try again.', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-16 min-h-screen">
      <section className="bg-gradient-to-br from-[#6b47dc] via-[#7c3aed] to-[#ff5722] text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-3">Get in touch</h1>
          <p className="text-lg opacity-90 max-w-xl">Have questions about our courses, corporate training or partnerships? Our team is here to help.</p>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-4 md:px-8 py-14 grid lg:grid-cols-[1fr_400px] gap-10">
        <div className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm">
          <h2 className="text-2xl font-extrabold mb-1">Send us a message</h2>
          <p className="text-sm text-gray-500 mb-6">We reply within 24 hours.</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Full name *</label>
                <div className="relative">
                  <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 focus:border-[#6b47dc] focus:ring-2 focus:ring-purple-100 outline-none" placeholder="Jane Doe" />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Email *</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 focus:border-[#6b47dc] focus:ring-2 focus:ring-purple-100 outline-none" placeholder="you@example.com" />
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Phone</label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 focus:border-[#6b47dc] focus:ring-2 focus:ring-purple-100 outline-none" placeholder={settings.contactPhone || '+91 98765 43210'} />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Interested course</label>
                <select value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })} className="w-full px-3 py-3 rounded-xl border border-gray-200 focus:border-[#6b47dc] focus:ring-2 focus:ring-purple-100 outline-none bg-white">
                  <option value="">Select a course</option>
                  <option>Data Science</option><option>Cloud Computing</option><option>DevOps</option><option>AI &amp; Machine Learning</option><option>Cyber Security</option><option>Other</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Message *</label>
              <div className="relative">
                <MessageSquare size={16} className="absolute left-3 top-4 text-gray-400" />
                <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-200 focus:border-[#6b47dc] focus:ring-2 focus:ring-purple-100 outline-none resize-none" placeholder="Tell us more about how we can help" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="bg-[#ff5722] hover:bg-[#e64a19] text-white font-bold h-12 px-8 btn-shine">{loading ? 'Sending...' : <>Send message <Send size={16} className="ml-1" /></>}</Button>
          </form>
        </div>

        <aside className="space-y-4">
          <div className="bg-gradient-to-br from-purple-50 to-orange-50 rounded-2xl p-6 border border-purple-100">
            <FloatingBadge color="#6b47dc" height="200px" />
            <div className="text-center mt-2">
              <div className="font-extrabold text-lg">Talk to an expert</div>
              <p className="text-sm text-gray-600 mt-1">Get personalized course recommendations.</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
            {settings.contactPhone && (
              <div className="flex gap-3 items-start">
                <div className="w-10 h-10 rounded-lg bg-purple-100 grid place-items-center flex-shrink-0"><Phone size={18} className="text-[#6b47dc]" /></div>
                <div><div className="font-bold">Call us</div><div className="text-sm text-gray-600">{settings.contactPhone}</div><div className="text-xs text-gray-400">{settings.contactHours || 'Mon-Sun, 24x7'}</div></div>
              </div>
            )}
            {settings.contactEmail && (
              <div className="flex gap-3 items-start">
                <div className="w-10 h-10 rounded-lg bg-orange-100 grid place-items-center flex-shrink-0"><Mail size={18} className="text-[#ff5722]" /></div>
                <div><div className="font-bold">Email us</div><div className="text-sm text-gray-600">{settings.contactEmail}</div><div className="text-xs text-gray-400">Reply within 24 hours</div></div>
              </div>
            )}
            {settings.contactAddress && (
              <div className="flex gap-3 items-start">
                <div className="w-10 h-10 rounded-lg bg-purple-100 grid place-items-center flex-shrink-0"><MapPin size={18} className="text-[#6b47dc]" /></div>
                <div><div className="font-bold">Visit us</div><div className="text-sm text-gray-600">{settings.contactAddress}</div></div>
              </div>
            )}
          </div>
        </aside>
      </section>
    </div>
  );
}
